import {
  autoUpdate,
  flip,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { useCallback, useState } from "react";
import { Editor, Range, Transforms } from "slate";
import { ReactEditor } from "slate-react";

import {
  applyIndent,
  applyOutdent,
  Button,
  getLinkAtSelection,
  getLinkDisplayTextAtSelection,
  getParagraphTag,
  hasMixedParagraphStyles,
  hotkeyLabel,
  isBlockActive,
  isMarkActive,
  marks,
  modals,
  Select,
  setParagraphTag,
  toggleBlockquote,
  toggleBulletedList,
  toggleMark,
  toggleNumberedList,
  Tooltip,
  TextStyleTag,
  textStyleOptions,
} from "@smart/itops-sb-design-system-dom";

import { useEditing } from "./context";
import { LinkPopup } from "./link-popup";

export const EditingToolbar = () => {
  const {
    activeEditor,
    saveContent,
    setCurrentSelection,
    popupContainerRef,
    lastEditorRef,
    lastSelectionRef,
    currentSelection,
    onListActivated,
  } = useEditing();
  const [linkPopupOpen, setLinkPopupOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState<string | undefined>(undefined);
  const [linkDisplayText, setLinkDisplayText] = useState("");
  const [isEditingExistingLink, setIsEditingExistingLink] = useState(false);
  const [linkSelection, setLinkSelection] = useState<Range | null>(null);

  const getCurrentTextStyleTag = useCallback((): TextStyleTag | null => {
    if (!activeEditor) return "p";

    const editorForStyle = activeEditor ?? lastEditorRef.current;
    const selectionForStyle =
      currentSelection ?? activeEditor.selection ?? lastSelectionRef.current;

    if (!editorForStyle || !selectionForStyle) return null;

    if (hasMixedParagraphStyles(editorForStyle, selectionForStyle)) return null;

    const rawTag = getParagraphTag(editorForStyle, selectionForStyle);
    return rawTag === "h1" || rawTag === "h2" || rawTag === "h3" ? rawTag : "p";
  }, [activeEditor, currentSelection, lastEditorRef, lastSelectionRef]);

  const handleTextStyleChange = (value: TextStyleTag) => {
    const editor = activeEditor ?? lastEditorRef.current;
    if (!editor) return;

    const selection = editor.selection ?? lastSelectionRef.current;
    if (selection) Transforms.select(editor, selection);

    setParagraphTag(editor, value, selection ?? undefined)();
    ReactEditor.focus(editor);

    if (editor.selection)
      setCurrentSelection({
        anchor: { ...editor.selection.anchor },
        focus: { ...editor.selection.focus },
      });
  };

  const handleIndent = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const editor = activeEditor ?? lastEditorRef.current;
    if (!editor) return;
    const selection = editor.selection ?? lastSelectionRef.current;
    if (selection) Transforms.select(editor, selection);
    applyIndent(editor, selection ?? undefined);
    ReactEditor.focus(editor);
  };

  const handleOutdent = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const editor = activeEditor ?? lastEditorRef.current;
    if (!editor) return;
    const selection = editor.selection ?? lastSelectionRef.current;
    if (selection) Transforms.select(editor, selection);
    applyOutdent(editor, selection ?? undefined);
    ReactEditor.focus(editor);
  };

  const linkFloating = useFloating({
    placement: "bottom",
    open: linkPopupOpen,
    onOpenChange: setLinkPopupOpen,
    middleware: [
      shift({ padding: 4 }),
      flip({ fallbackPlacements: ["bottom", "top"] }),
    ],
    whileElementsMounted: autoUpdate,
  });
  const linkPopupInteractions = useInteractions([
    useDismiss(linkFloating.context),
  ]);

  const handleBold = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (activeEditor) toggleMark(activeEditor, "bold")(e as React.MouseEvent);
  };

  const handleItalic = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (activeEditor) toggleMark(activeEditor, "italic")(e as React.MouseEvent);
  };

  const handleUnderline = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (activeEditor)
      toggleMark(activeEditor, "underline")(e as React.MouseEvent);
  };

  const isLinkDisabled =
    !activeEditor || isBlockActive(activeEditor, ["placeholder"]);

  const handleLinkClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!activeEditor || isLinkDisabled) return;
    const editor = activeEditor ?? lastEditorRef.current;
    if (!editor) return;
    const selection =
      currentSelection ?? activeEditor.selection ?? lastSelectionRef.current;

    const linkEntry = selection
      ? getLinkAtSelection(editor, selection)
      : undefined;
    const isEditingLink = !!linkEntry;

    const selectedText =
      selection && Range.isExpanded(selection)
        ? Editor.string(editor, selection)
        : "";

    setIsEditingExistingLink(isEditingLink);
    setLinkUrl(isEditingLink ? linkEntry[0].url : undefined);
    setLinkDisplayText(
      isEditingLink && selection
        ? (getLinkDisplayTextAtSelection(editor, selection) ?? "")
        : selectedText,
    );
    setLinkSelection(selection);
    setLinkPopupOpen(true);
  };

  const handleLinkPopupClose = () => {
    popupContainerRef.current = null;
    setLinkUrl(undefined);
    setLinkDisplayText("");
    setIsEditingExistingLink(false);
    setLinkSelection(null);
    setLinkPopupOpen(false);
    const editor = activeEditor ?? lastEditorRef.current;
    if (editor?.selection) {
      setCurrentSelection({
        anchor: { ...editor.selection.anchor },
        focus: { ...editor.selection.focus },
      });
    } else {
      setCurrentSelection(null);
    }
  };

  const linkPopupFloatingProps =
    linkPopupOpen && activeEditor
      ? linkPopupInteractions.getFloatingProps()
      : null;

  return (
    <div
      className="flex flex-row items-center gap-medium bg-white px-[2.4rem] py-[0.8rem] border rounded-[0.8rem] border-neutral-100 shadow-modal"
      data-testid="editing-toolbar"
      role="toolbar"
      aria-label="Text formatting"
      onMouseDown={(e) => e.preventDefault()}
      onTouchStart={(e) => e.preventDefault()}
    >
      <Tooltip
        id="editing-toolbar-text-style"
        placement="top"
        text="Text Style"
        offsetBy={5}
        variant="paragraphy"
      >
        <Select
          value={getCurrentTextStyleTag() ?? undefined}
          onChange={handleTextStyleChange}
          options={textStyleOptions}
          disabled={!activeEditor}
          dataTestId="editing-toolbar-text-style"
          className="w-[12rem]"
          compact
        />
      </Tooltip>
      <Tooltip
        id="editing-toolbar-bold"
        placement="top"
        text={`Bold${activeEditor ? `\n${hotkeyLabel(marks.bold.hotkey)}` : ""}`}
        offsetBy={5}
        variant="paragraphy"
      >
        <Button
          variant="inlineSubtle"
          leftIcon="regularBold"
          dataTestId="editing-toolbar-bold"
          disabled={!activeEditor}
          isPressed={!!activeEditor && isMarkActive(activeEditor, "bold")}
          onMouseDown={handleBold}
          onTouchStart={handleBold}
        />
      </Tooltip>
      <Tooltip
        id="editing-toolbar-italic"
        placement="top"
        text={`Italics${activeEditor ? `\n${hotkeyLabel(marks.italic.hotkey)}` : ""}`}
        offsetBy={5}
        variant="paragraphy"
      >
        <Button
          variant="inlineSubtle"
          leftIcon="regularItalic"
          dataTestId="editing-toolbar-italic"
          disabled={!activeEditor}
          isPressed={!!activeEditor && isMarkActive(activeEditor, "italic")}
          onMouseDown={handleItalic}
          onTouchStart={handleItalic}
        />
      </Tooltip>
      <Tooltip
        id="editing-toolbar-underline"
        placement="top"
        text={`Underline${activeEditor ? `\n${hotkeyLabel(marks.underline.hotkey)}` : ""}`}
        offsetBy={5}
        variant="paragraphy"
      >
        <Button
          variant="inlineSubtle"
          leftIcon="regularUnderline"
          dataTestId="editing-toolbar-underline"
          disabled={!activeEditor}
          isPressed={!!activeEditor && isMarkActive(activeEditor, "underline")}
          onMouseDown={handleUnderline}
          onTouchStart={handleUnderline}
        />
      </Tooltip>
      <Tooltip
        id="editing-toolbar-link"
        placement="top"
        text={
          isLinkDisabled
            ? undefined
            : `${modals.link.text}\n${hotkeyLabel(modals.link.hotkey)}`
        }
        offsetBy={5}
        variant="paragraphy"
      >
        <div
          ref={linkFloating.refs.setReference}
          {...linkPopupInteractions.getReferenceProps()}
        >
          <Button
            variant="inlineSubtle"
            leftIcon={linkPopupOpen ? "solidLink" : "regularLink"}
            dataTestId="editing-toolbar-link"
            disabled={isLinkDisabled}
            isPressed={linkPopupOpen}
            onMouseDown={handleLinkClick}
            onTouchStart={handleLinkClick}
          />
        </div>
      </Tooltip>
      <Tooltip
        id="editing-toolbar-blockquote"
        placement="top"
        text="Accent Line"
        offsetBy={5}
        variant="paragraphy"
      >
        <Button
          variant="inlineSubtle"
          leftIcon="regularBlockQuote"
          dataTestId="editing-toolbar-blockquote"
          disabled={!activeEditor}
          isPressed={
            !!activeEditor && isBlockActive(activeEditor, ["blockquote"])
          }
          onMouseDown={(e) => {
            e.preventDefault();
            if (activeEditor) toggleBlockquote(activeEditor)(e);
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            if (activeEditor) toggleBlockquote(activeEditor)(e);
          }}
        />
      </Tooltip>
      <Tooltip
        id="editing-toolbar-outdent"
        placement="top"
        text="Decrease Indent"
        offsetBy={5}
        variant="paragraphy"
      >
        <Button
          variant="inlineSubtle"
          leftIcon="regularOutdent"
          dataTestId="editing-toolbar-outdent"
          disabled={!activeEditor}
          onMouseDown={handleOutdent}
          onTouchStart={handleOutdent}
        />
      </Tooltip>
      <Tooltip
        id="editing-toolbar-indent"
        placement="top"
        text="Increase Indent"
        offsetBy={5}
        variant="paragraphy"
      >
        <Button
          variant="inlineSubtle"
          leftIcon="regularIndent"
          dataTestId="editing-toolbar-indent"
          disabled={!activeEditor}
          onMouseDown={handleIndent}
          onTouchStart={handleIndent}
        />
      </Tooltip>
      <Tooltip
        id="editing-toolbar-list-numbered"
        placement="top"
        text="Numbered List"
        offsetBy={5}
        variant="paragraphy"
      >
        <Button
          variant="inlineSubtle"
          leftIcon="regularList12"
          dataTestId="editing-toolbar-list-numbered"
          disabled={!activeEditor}
          isPressed={
            !!activeEditor && isBlockActive(activeEditor, ["numbered-list"])
          }
          onMouseDown={(e) => {
            e.preventDefault();
            if (activeEditor) {
              const isActivating = !isBlockActive(activeEditor, [
                "numbered-list",
              ]);
              toggleNumberedList(activeEditor)(e);
              if (isActivating) onListActivated();
            }
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            if (activeEditor) {
              const isActivating = !isBlockActive(activeEditor, [
                "numbered-list",
              ]);
              toggleNumberedList(activeEditor)(e);
              if (isActivating) onListActivated();
            }
          }}
        />
      </Tooltip>
      <Tooltip
        id="editing-toolbar-list-bullet"
        placement="top"
        text="Bulleted List"
        offsetBy={5}
        variant="paragraphy"
      >
        <Button
          variant="inlineSubtle"
          leftIcon="regularListUl"
          dataTestId="editing-toolbar-list-bullet"
          disabled={!activeEditor}
          isPressed={
            !!activeEditor && isBlockActive(activeEditor, ["bulleted-list"])
          }
          onMouseDown={(e) => {
            e.preventDefault();
            if (activeEditor) {
              const isActivating = !isBlockActive(activeEditor, [
                "bulleted-list",
              ]);
              toggleBulletedList(activeEditor)(e);
              if (isActivating) onListActivated();
            }
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            if (activeEditor) {
              const isActivating = !isBlockActive(activeEditor, [
                "bulleted-list",
              ]);
              toggleBulletedList(activeEditor)(e);
              if (isActivating) onListActivated();
            }
          }}
        />
      </Tooltip>
      {linkPopupOpen && activeEditor && linkPopupFloatingProps && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <span
          ref={(el) => {
            popupContainerRef.current = el;
            linkFloating.refs.setFloating(el);
          }}
          className="block bg-white rounded shadow-dropdownMenu z-20 text-ui border border-neutral-100"
          style={linkFloating.floatingStyles}
          {...linkPopupFloatingProps}
          onMouseDown={(e) => {
            e.stopPropagation();
            const { onMouseDown } = linkPopupFloatingProps;
            if (typeof onMouseDown === "function") {
              onMouseDown(e);
            }
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            const { onTouchStart } = linkPopupFloatingProps;
            if (typeof onTouchStart === "function") {
              onTouchStart(e);
            }
          }}
        >
          <LinkPopup
            editor={activeEditor}
            initialUrl={linkUrl}
            initialDisplayText={linkDisplayText}
            isEditingExistingLink={isEditingExistingLink}
            selection={linkSelection}
            onClose={handleLinkPopupClose}
            onChanged={saveContent}
          />
        </span>
      )}
    </div>
  );
};
